import { ChangeEvent } from 'react';

import {
  Button,
  IconButton,
  MenuItem,
  Select,
  Theme,
  makeStyles,
} from '@material-ui/core';

import {
  RiArrowLeftSLine as ChevronLeft,
  RiArrowRightSLine as ChevronRight,
} from '@remixicon/react';
import classNames from 'classnames';
import { Duration, add } from 'date-fns';
import startOfDay from 'date-fns/startOfDay';
import mapValues from 'lodash/mapValues';

type TimeControlProps = {
  className?: string;
  originDate: Date;
  onOriginDateChange: (newDate: Date) => void;
  timeRange: TimeRange;
  onTimeRangeChange: (newTimeRange: TimeRange) => void;
  timeWindowJumpSize: Duration;
};

const useStyles = makeStyles((theme: Theme) => ({
  timeControls: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    padding: theme.spacing(0, 2),
  },
  timeNavigationArrows: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  timeRangeSelect: {
    width: 120,
  },
}));

const timeRanges = ['Week', 'Month', 'Quarter', 'Year'] as const;
export type TimeRange = (typeof timeRanges)[number];

export const TimeControls = ({
  originDate,
  onOriginDateChange,
  timeRange,
  onTimeRangeChange,
  timeWindowJumpSize,
  className,
}: TimeControlProps) => {
  const classes = useStyles();
  const today = startOfDay(new Date());

  const resetTime = () => {
    onOriginDateChange(today);
  };

  const handleTimeRangeChange = (e: ChangeEvent<{ value: unknown }>) => {
    onTimeRangeChange(e.target.value as TimeRange);
  };

  const jumpBack = () => {
    onOriginDateChange(
      add(
        originDate,
        mapValues(timeWindowJumpSize, amount => -1 * (amount ?? 0)),
      ),
    );
  };

  const jumpForward = () => {
    onOriginDateChange(add(originDate, timeWindowJumpSize));
  };

  return (
    <div className={classNames(classes.timeControls, className)}>
      <div className={classes.timeNavigationArrows}>
        <IconButton size="small" onClick={jumpBack} data-track-source-anchor="ce219af021;time-controls-icon-button;low">
          <ChevronLeft />
        </IconButton>
        <IconButton size="small" onClick={jumpForward} data-track-source-anchor="86fcf3a6f4;time-controls-icon-button-2;low">
          <ChevronRight />
        </IconButton>
      </div>
      <Select
        value={timeRange}
        className={classes.timeRangeSelect}
        onChange={handleTimeRangeChange}
        variant="outlined"
        margin="dense"
       data-track-source-anchor="266cb37700;time-controls-select">
        {timeRanges.map(range => (
          <MenuItem key={range} value={range} data-track-source-anchor="c93b324d00;time-controls-menu-item;low">
            {range}
          </MenuItem>
        ))}
      </Select>
      <Button onClick={resetTime} data-track-source-anchor="d140b114dc">Today</Button>
    </div>
  );
};
